import { Injectable } from '@angular/core';
import { catchError, first, mergeMap, tap } from 'rxjs/operators';
import { ReportsApiService } from './api.service';
import { of } from 'rxjs';
import { IReportsState } from './state';
import { HttpErrorResponse } from '@angular/common/http';
import { getCustomGroupReportReq, getODNoticeReq } from './selector';
import { initialReportsState } from 'src/app/store/reports/initialState';
import { Lux } from '@roosevelt/common-ui-lib/core';

@Injectable({
  providedIn: 'root'
})
export class ReportsAsync {
  constructor(
    private readonly lux: Lux<{ reportsState: IReportsState }>,
    private readonly reportsService: ReportsApiService,
  ) {
  }

  // overage Dependent Notice
  getODNotice() {
    return this.lux.get(getODNoticeReq).pipe(
      first(),
      tap(() => this.setReportsState({ isFetchingOverageDepNotice: true, overageDepNoticeError: '' })),
      mergeMap(x => this.reportsService.getODNotice(x)),
      tap(results => {
        this.setReportsState({ overageDepNoticeRes: results, isFetchingOverageDepNotice: false });
      }),
      catchError((err: HttpErrorResponse) => {
        const errorMessage = this.extractApiErrorMessage(err);
        this.setReportsState({ overageDepNoticeError: errorMessage, isFetchingOverageDepNotice: false });
        return of(errorMessage);
      })
    );
  }

  // get pdf Document
  getPDFDocument(req) {
    return this.fetchDocument(req, 'application/pdf');
  }

  // Adhoc Report for the Overage Dependent
  getAdhocReport(req) {
    return of(req).pipe(
      first(),
      tap(() => this.setReportsState({ isFetchingAdhocReport: true, adhocReportError: '' })),
      mergeMap(x => this.reportsService.getAdhocReport(x)),
      tap(() => {
        this.setReportsState({ isFetchingAdhocReport: false });
      }),
      catchError((err: HttpErrorResponse) => {
        const errorMessage = this.extractApiErrorMessage(err);
        this.setReportsState({ adhocReportError: errorMessage, isFetchingAdhocReport: false });
        return of(errorMessage);
      })
    );
  }

  // Consolidated Report for the Overage Dependent
  getConsolidatedReport(req) {
    return of(req).pipe(
      first(),
      tap(() => this.setReportsState({ isFetchingConsolidatedReport: true, consolidatedReportErrors: '' })),
      mergeMap(x => this.reportsService.getConsolidatedReport(x)),
      tap(() => {
        this.setReportsState({ isFetchingConsolidatedReport: false });
      }),
      catchError((err: HttpErrorResponse) => {
        const errorMessage = this.extractApiErrorMessage(err);
        this.setReportsState({ consolidatedReportErrors: errorMessage, isFetchingConsolidatedReport: false });
        return of(errorMessage);
      })
    );
  }

  // custom group reports
  getCustomGroupReports() {
    return this.lux.get(getCustomGroupReportReq).pipe(
      first(),
      tap(() => this.setReportsState({
        isFetchingCustomGroupReport: true,
        customGroupReportError: '',
        errorFetchingDocument: '',
        customGroupReportRes: initialReportsState.customGroupReportRes
      })),
      mergeMap(x => this.reportsService.getCustomGroupReport(x)),
      tap(results => {
        this.setReportsState({ customGroupReportRes: results, isFetchingCustomGroupReport: false });
      }),
      catchError((err: HttpErrorResponse) => {
        const errorMessage = this.extractApiErrorMessage(err);
        this.setReportsState({ customGroupReportError: errorMessage, isFetchingCustomGroupReport: false });
        return of(errorMessage);
      })
    );
  }

  // get excel Document
  getExcelDocument(req) {
    return this.fetchDocument(req, 'application/vnd.ms-excel');
  }

  private fetchDocument(req: unknown, type: string) {
    return of(req).pipe(
      first(),
      tap(() => this.setReportsState({ isFetchingDocument: true, errorFetchingDocument: '' })),
      mergeMap(x => this.reportsService.getDocument(x)),
      tap(res => {
        this.setReportsState({ isFetchingDocument: false });
        this.openBlobDocument(res, type);
      }),
      catchError((e: HttpErrorResponse) => {
        const err = extractDocumentErrorMessage(e.error, e.message);
        this.setReportsState({ errorFetchingDocument: err, isFetchingDocument: false });
        return of(err);
      })
    );
  }

  private setReportsState(statePatch: Partial<IReportsState>): void {
    this.lux.set(x => x.reportsState, statePatch);
  }

  private extractApiErrorMessage(err: HttpErrorResponse): string {
    const apiErrorList = err?.error?.apiErrorList;
    if (!apiErrorList?.length) {
      return '';
    }

    return apiErrorList.map(item => item?.errorMessage || '').filter(Boolean).join('\n');
  }

  private openBlobDocument(blobPart: BlobPart, type: string): void {
    const file = new Blob([blobPart], { type });
    const fileURL = URL.createObjectURL(file);
    window.open(fileURL);
  }
}

type ReportApiError = {
  apiErrorList?: Array<{
    errorMessage?: string;
  }>;
  message?: string;
};

export function extractDocumentErrorMessage(errorBody: unknown, fallbackMessage = ''): string {
  const parsedError = bufferToString(errorBody);
  if (typeof parsedError === 'string') {
    return parsedError || fallbackMessage || 'Unable to open report document.';
  }

  if (parsedError.apiErrorList?.length) {
    return parsedError.apiErrorList
      .map(item => item.errorMessage)
      .filter((message): message is string => !!message)
      .join('\n');
  }

  if (parsedError?.message) {
    return parsedError.message;
  }

  return fallbackMessage || 'Unable to open report document.';
}

export function bufferToString(buf: unknown): ReportApiError | string {
  if (buf == null) {
    return '';
  }

  if (typeof buf === 'string') {
    return safelyParseJson(buf);
  }

  if (buf instanceof ArrayBuffer) {
    return safelyParseJson(decodeArrayBuffer(buf));
  }

  if (ArrayBuffer.isView(buf)) {
    return safelyParseJson(new TextDecoder('utf-8').decode(new Uint8Array(buf.buffer, buf.byteOffset, buf.byteLength)).trim());
  }

  if (typeof buf === 'object') {
    return buf as ReportApiError;
  }

  if (typeof buf === 'number' || typeof buf === 'boolean' || typeof buf === 'bigint' || typeof buf === 'symbol') {
    return String(buf);
  }

  return '';
}

function decodeArrayBuffer(buffer: ArrayBuffer): string {
  if (!buffer.byteLength) {
    return '';
  }

  return new TextDecoder('utf-8').decode(new Uint8Array(buffer)).trim();
}

function safelyParseJson(value: string): ReportApiError | string {
  const normalizedValue = value.trim();
  if (!normalizedValue) {
    return '';
  }

  try {
    return JSON.parse(normalizedValue) as ReportApiError;
  } catch {
    return normalizedValue;
  }
}
